import { Component } from 'react';

import { LoginDialogMode } from '../../components/ui/login/login-modal.component';
import LoginComponent from '../../components/ui/login/login.component';
import { lazyInject } from '../../context/module';
import { ApiConfig } from '../../services/api-config';
import { LightHttp } from '../../services/light-http.service';
import { LoginService } from '../../services/login.service';
import { OfflineTaskService } from '../../services/offline-task.service';
import { ScriptLoadService } from '../../services/script-load.service';
import { UIContext } from '../../services/ui-context';
import { AccountDetails, PaymentStateTypes, ResponsePipe } from '../../utils/request.utils';
import PortalLayout from './layout/portal-layout.component';
import PortalLoading from './layout/portal-loading.component';
import PortalPageHeader from './layout/portal-page-header.component';
import { CancelModal, type CancelReason } from './plans/cancel-modal.component';
import styles from './portal.component.module.scss';

interface PortalComponentState {
    cancelModalOpen: boolean;
}

/**
 * Portal landing page — "My Account" at `/portal`.
 *
 * Polished in Phase 7 to use the shared portal shell components
 * (header / loading / empty-state) and §7.0 design tokens. The cancel-
 * subscription button stays as-is — the rest of the page is presentation.
 */
export default class PortalComponent extends Component<unknown, PortalComponentState> {
    userDetails: AccountDetails;
    loginViewMode: LoginDialogMode = 'login';
    ready = false;

    isPaidUser = false;

    state: PortalComponentState = {
        cancelModalOpen: false,
    };

    get isUserLoggedIn() {
        return this.loginService.isUserLoggedIn;
    }

    get canGoOffline() {
        return this.offlineTaskService.getCanGoOfflineMode();
    }

    // Cancelled subscriptions are active until end of billing cycle
    get isSubscriptionActive() {
        return this.isPaidUser;
    }

    /**
     * True when the user has explicitly cancelled a paid subscription.
     * The legacy User document uses `paymentState = "cancelled"` to mark
     * an active subscription that the user has cancelled but is still
     * within the grace period. `"unknown"` (the default for users who
     * never subscribed) and `"signup"` are NOT cancellations — they
     * just mean the user has no subscription to begin with.
     */
    get isPaymentCancelled() {
        return this.userDetails?.paymentState === PaymentStateTypes.cancelled;
    }

    /** True iff the user has never held a paid subscription. */
    get hasNeverSubscribed() {
        const ps = this.userDetails?.paymentState;
        return !this.isPaidUser
            && (ps === PaymentStateTypes.unknown
                || ps === PaymentStateTypes.signup
                || !ps);
    }

    get subscriptionName() {
        if (!this.userDetails) {
            return '';
        }
        if (this.userDetails.packageType === 'PACKAGE_TYPE_BASIC') {
            return 'Basic';
        } else if (this.userDetails.packageType === 'PACKAGE_TYPE_PRO') {
            return 'Pro';
        }
        return 'Free';
    }

    get subscriptionStatusReadable() {
        if (this.isPaymentCancelled && this.isSubscriptionActive) {
            return 'Cancelled (active until ' + this.userDetails.subscriptionNextBillingDate + ')';
        }
        if (this.isPaymentCancelled) {
            return 'Cancelled';
        }
        if (this.isSubscriptionActive) {
            return 'Active';
        }
        if (this.hasNeverSubscribed) {
            return 'No subscription';
        }
        return 'Inactive';
    }

    @lazyInject(ApiConfig) private apiConfig: ApiConfig;
    @lazyInject(LoginService) private loginService: LoginService;
    @lazyInject(LightHttp) private http: LightHttp;
    @lazyInject(OfflineTaskService) private offlineTaskService: OfflineTaskService;
    @lazyInject(UIContext) private uiContext: UIContext;
    @lazyInject(ScriptLoadService) private scriptLoadService: ScriptLoadService;

    constructor(props) {
        super(props);
        this.getUserDetails();
        this.scriptLoadService.loadAllScriptsIfNotLoaded(true);
    }

    updateUserDetails(mode: string) {
        if (mode === 'register') {
            this.loginViewMode = 'register';
        }
        if (this.loginService.isUserLoggedIn) {
            this.getUserDetails();
        }
    }

    getUserDetails() {
        this.loginService.onUserDetailsChanged.subscribe(
            data => {
                this.userDetails = data;
                this.ready = true;
                if (data) {
                    this.isPaidUser = this.userDetails.isPaidUser();
                }
                this.forceUpdate();
            },
            (err) => {
                console.error(err);
                this.ready = true;
                this.forceUpdate();
            },
        );
    }

    cancelSubscription() {
        this.setState({cancelModalOpen: true});
    }

    private closeCancelModal = (): void => {
        this.setState({cancelModalOpen: false});
    };

    private onCancelConfirm = ({reason}: {reason: CancelReason | null}): void => {
        this.http.post(this.apiConfig.getCancelPaymentsEndpoint(), {reason}).pipe(
            ResponsePipe,
        ).subscribe({
            next: () => {
                this.uiContext.successToast('Subscription cancelled successfully.');
                this.setState({cancelModalOpen: false});
                setTimeout(() => location.reload(), 2000);
            },
            error: (err) => {
                this.uiContext.handleError(err);
                this.setState({cancelModalOpen: false});
                this.forceUpdate();
            },
        });
    };

    private getCancelModalActiveUntil(): string | null {
        const next = this.userDetails?.subscriptionNextBillingDate;
        if (!next) return null;
        try {
            return next instanceof Date ? next.toISOString() : String(next);
        } catch {
            return null;
        }
    }

    private subscriptionStateClass(): string {
        if (this.isPaymentCancelled && this.isSubscriptionActive) return styles.stateCancelled;
        if (this.isPaymentCancelled) return styles.stateCancelled;
        if (this.isSubscriptionActive) return styles.stateActive;
        return styles.stateInactive;
    }

    private renderLoggedInBody() {
        const header = (
            <PortalPageHeader
                title="My Account"
                subtitle="Profile details, plan, and subscription management."
                breadcrumbs={[{label: 'Portal'}, {label: 'My Account'}]}
            />
        );

        return (
            <>
                {header}
                <div className={styles.pageBody}>
                    <section className={styles.section}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Profile</h2>
                            </div>
                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Name</span>
                                    <span className={styles.fieldValue}>
                                        {this.userDetails?.name || 'Not provided'}
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Email</span>
                                    <span className={styles.fieldValue}>
                                        {this.userDetails?.email || '—'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className={styles.section}>
                        <div className={styles.card}>
                            <div className={styles.cardHeader}>
                                <h2 className={styles.cardTitle}>Subscription</h2>
                            </div>
                            <div className={styles.fieldRow}>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Plan</span>
                                    <span className={styles.fieldValue}>
                                        {this.subscriptionName} plan
                                    </span>
                                </div>
                                <div className={styles.field}>
                                    <span className={styles.fieldLabel}>Status</span>
                                    <span
                                        className={`${styles.statePill} ${this.subscriptionStateClass()}`}
                                    >
                                        {this.subscriptionStatusReadable}
                                    </span>
                                </div>
                            </div>

                            {!this.canGoOffline && (
                                <div
                                    className={styles.upgradeCallout}
                                    style={{marginTop: 20}}
                                >
                                    <div className={styles.upgradeCopy}>
                                        <h3>You're on the Free tier</h3>
                                        <p>
                                            Upgrade to unlock higher limits, the API,
                                            and monthly credit grants.
                                        </p>
                                    </div>
                                    <a className={styles.upgradeBtn} href="/product">
                                        View plans
                                    </a>
                                </div>
                            )}

                            {!this.isPaymentCancelled && this.isSubscriptionActive && (
                                <div className={styles.cancelCard}>
                                    <div className={styles.cancelCopy}>
                                        <p className={styles.cancelTitle}>
                                            Cancel subscription
                                        </p>
                                        <p className={styles.cancelHint}>
                                            Access continues until the end of your
                                            billing cycle.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        className={styles.cancelBtn}
                                        onClick={() => this.cancelSubscription()}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>

                    <div className={styles.supportFootnote}>
                        Need help?{' '}
                        <a href="mailto:support@xconvert.com">
                            support@xconvert.com
                        </a>
                    </div>
                </div>
            </>
        );
    }

    private renderLoggedOutBody() {
        return (
            <>
                <PortalPageHeader
                    title="Welcome back"
                    subtitle="Sign in to manage your account, plan, and developer credentials."
                />
                <div className={styles.pageBody}>
                    <div className={styles.loginCard}>
                        <div className={styles.loginHeading}>
                            <h2>Sign in</h2>
                            <p>Use your xconvert email and password to continue.</p>
                        </div>
                        <div className={styles.loginWrapper}>
                            <LoginComponent
                                passwordResetEnabled={true}
                                registerEnabled={true}
                                submitBtnText={'Log In'}
                                mode={this.loginViewMode}
                                onSuccess={($event) => this.updateUserDetails($event)}
                            />
                        </div>
                    </div>
                </div>
            </>
        );
    }

    getHTML() {
        if (this.isUserLoggedIn) {
            return this.renderLoggedInBody();
        }
        if (!this.isUserLoggedIn && this.ready) {
            return this.renderLoggedOutBody();
        }
        return (
            <>
                <PortalPageHeader title="Loading…" />
                <PortalLoading message="Loading your account…" />
            </>
        );
    }

    render() {
        return (
            <PortalLayout activePath="/portal">
                {this.getHTML()}
                {this.state.cancelModalOpen && (
                    <CancelModal
                        open
                        productLabel={this.subscriptionName || 'subscription'}
                        productSummary="Subscription benefits"
                        activeUntil={this.getCancelModalActiveUntil()}
                        onKeep={this.closeCancelModal}
                        onConfirm={this.onCancelConfirm}
                    />
                )}
            </PortalLayout>
        );
    }
}
